import { makeClient } from '@spree/storefront-api-v2-sdk';
import MicroModal from 'micromodal';
import { logErrorToSentry } from '../helpers/helpers';

import {
  setupRequestSampleModal,
  getSelectedSampleTypeProperties,
} from './dom';

import { fireAdobeAnalyticsEvent } from '../helpers/adobe-analytics';
import { pushDataLayerEvent } from '../helpers/data-layer';

// Some upstream proxies return 200 with an HTML error page (typically starting
// with "<font dir=..."). response.json() then throws a useless "Unexpected
// token '<'" SyntaxError. Read as text first and rethrow with URL + status +
// body snippet so Sentry shows what actually came back.
const safeJson = async response => {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch (_) {
    throw new Error(
      `Expected JSON from ${response.url} (status ${
        response.status
      }), got: ${text.slice(0, 80)}`
    );
  }
};

const setGlobalTokens = (orderToken, oauthToken) => {
  const result = {
    orderToken,
    oauthToken,
  };

  window.SpreeAPI = { ...window.SpreeAPI, ...result };

  return result;
};

// One fetch serves both consumers: Adobe takes the delimited products string,
// GA4 needs a readable item_name, and the product resource carries both.
export const getProductAnalyticsData = async variantId => {
  if (!window?.SpreeAPI?.host) return;

  const url = `${window.SpreeAPI.host}/api/v2/storefront/products/${variantId}?include=product_properties,vendor`;
  const headers = { Accept: 'application/vnd.api+json' };

  try {
    const response = await fetch(url, { method: 'GET', headers });
    const json = await safeJson(response);

    let analyticsName = '';
    let vendor = '';
    if (Array.isArray(json?.included)) {
      const property = json.included.find(
        item =>
          item?.type === 'product_property' &&
          item?.attributes?.name === 'analytics_name'
      );
      const vendorItem = json.included.find(item => item?.type === 'vendor');

      analyticsName = property?.attributes?.value || '';
      vendor = vendorItem?.attributes?.name || '';
    }

    // Interpolating an absent value yields the literal "undefined", which
    // Adobe reports as a product of that name. An empty vendor segment is a
    // well-formed field in this taxonomy; a missing name leaves nothing.
    if (!analyticsName) return undefined;

    return {
      products: `${vendor};${analyticsName}`,
      vendor: vendor,
      name: json?.data?.attributes?.name,
    };
  } catch (error) {
    logErrorToSentry?.(error);
    return undefined;
  }
};

const sendAddToCartEvents = async variantId => {
  const analyticsData = await getProductAnalyticsData(variantId);
  if (!analyticsData) {
    console.warn('Missing analytics_name for variant:', variantId);
    return;
  }

  const cartId = window.analytics?.cart?.id || null;
  const eventData = {
    type: 'cart:add',
    detail: {
      products: analyticsData.products,
      ...(cartId && { cart: { id: cartId } }),
    },
  };
  fireAdobeAnalyticsEvent(eventData);

  // Samples are free, so value stays 0 — but the item array is what links
  // this into the ecommerce funnel rather than counting as a bare event.
  // item_name matches what cart reports for the same product.
  pushDataLayerEvent('add_to_cart', {
    currency: 'USD',
    value: 0,
    items: [
      {
        item_id: String(variantId),
        item_name: analyticsData.name,
        item_category: analyticsData.vendor,
        quantity: 1,
      },
    ],
  });
};

const getTokens = async () => {
  // Reuse tokens if they already exist to avoid unnecessary cross-origin calls.
  if (window.SpreeAPI.orderToken || window.SpreeAPI.oauthToken) {
    return {
      orderToken: window.SpreeAPI.orderToken,
      oauthToken: window.SpreeAPI.oauthToken,
    };
  }

  // Reading the cart must never create one: a visitor without a cart gets
  // null tokens here, and a failed read (CORS, proxy page, 5xx) is treated
  // the same. Only addToCart may POST /ensure_cart.
  try {
    const response = await fetch(`${window.SpreeAPI.host}/api_tokens`, {
      credentials: 'include',
    });
    if (response.ok) {
      const json = await safeJson(response);
      return setGlobalTokens(json.order_token, json.oauth_token);
    }
  } catch (_) {
    // Blocked cross-origin reads are expected; fall through to "no cart".
  }

  return { orderToken: undefined, oauthToken: undefined };
};

export const fetchCart = async () => {
  const tokens = await getTokens();

  if (!tokens.orderToken) return;

  const client = makeClient({ host: window.SpreeAPI.host });
  const response = await client.cart.show({ orderToken: tokens.orderToken });

  if (response.isSuccess()) return response.success();
  else console.warn(response.fail().summary);
};

export const checkProductAvailability = async productId => {
  const client = makeClient({ host: window.SpreeAPI.host });

  return await client.products.show(productId, {
    include: 'product_properties',
  });
};

export const addSelectedSampleTypeToCart = async quantity => {
  const selectedSampleTypeProperties = getSelectedSampleTypeProperties(),
    analyticsSampleType =
      selectedSampleTypeProperties.cartModalInfo.sampleType
        .toLowerCase()
        .replace(' sample', '') ||
      selectedSampleTypeProperties.analyticsTracker;

  // The legacy PDP markup's data-product-id is the Spree product, which is
  // what the cart:add products lookup takes.
  return await addToCart({
    variantId: selectedSampleTypeProperties.id,
    quantity,
    analyticsProductId: selectedSampleTypeProperties.id,
  });
};

export const addToCart = async details => {
  const { variantId, quantity, analyticsProductId } = details;
  let orderToken;
  const client = makeClient({ host: window.SpreeAPI.host });

  if (!window.SpreeAPI.orderToken) {
    const cartResponse = await fetch(`${window.SpreeAPI.host}/ensure_cart`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!cartResponse.ok) {
      const error = new Error(
        `ensure_cart failed with status ${cartResponse.status}`
      );
      logErrorToSentry?.(error);
      alert('Could not start a cart — please try again.');
      return;
    }

    const cart = await safeJson(cartResponse);

    setGlobalTokens(cart.token);
    orderToken = cart.token;
  } else {
    orderToken = window.SpreeAPI.orderToken;
  }

  const response = await client.cart.addItem(
    { orderToken },
    {
      variant_id: variantId,
      quantity,
    }
  );

  if (response.isSuccess()) {
    // ensure the cart id is captured and global analytics object is initiated
    if (!window.analytics?.cart?.id) {
      window.analytics = window.analytics || {};
      window.analytics.cart = window.analytics.cart || {};
      window.analytics.cart.id = response.success().data?.id;
    }
    MicroModal.show('modal-request-sample');
    setupRequestSampleModal();

    if (analyticsProductId) {
      sendAddToCartEvents(analyticsProductId);
    } else {
      console.warn('No analyticsProductId for variant:', variantId);
    }
    return response.success().data.attributes;
  } else {
    const error = new Error(response.fail().summary);
    window.Sentry.captureException(error);
    alert(response.fail().summary);
  }
};

export const freeSampleCounts = async (variantId = '') => {
  let orderToken;

  if (!window.SpreeAPI.orderToken) {
    const cartResponse = await fetch(`${window.SpreeAPI.host}/ensure_cart`, {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `X-Spree-Order-Token ${orderToken}` },
    });
    if (!cartResponse.ok) {
      logErrorToSentry?.(
        new Error(`ensure_cart failed with status ${cartResponse.status}`)
      );
      return;
    }

    const cart = await safeJson(cartResponse);

    setGlobalTokens(cart.token);
    orderToken = cart.token;
  } else {
    orderToken = window.SpreeAPI.orderToken;
  }

  const freeSampleCountUrl = `${window.SpreeAPI.host}/api/v2/storefront/free_sample_counts?variant_id=${variantId}`;
  const init = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Spree-Order-Token': orderToken,
    },
  };
  return await fetch(freeSampleCountUrl, init)
    .then(response => {
      if (response.ok) {
        return safeJson(response);
      }
    })
    .then(json => {
      return json;
    })
    .catch(error => {
      logErrorToSentry(error);
    });
};

export const fetchAccountLinks = async () => {
  return await fetch(`${window.SpreeAPI.host}/account_link`, {
    credentials: 'include',
    headers: {
      Authorization: `X-Spree-Order-Token ${window.SpreeAPI.orderToken}`,
    },
  })
    .then(response => response.ok && response.text())
    .catch(error => {
      logErrorToSentry(error);
    });
};
